/* @HEADER
 * @version 1.0.1465 | 2026-09-17
 * @purpose The Space-delete flow that App.tsx used to hold inline: the «✕» opens a question (after the refusals that are announced as events), the dialog's confirm is the only place that deletes, and the caller is told which Space went so it can leave the view. One hook, unchanged behaviour, so the flow can be driven in a test instead of read.
 * @sidecar useSpaceDeleteFlow.ts.header.md
 * @layer app | @hex _none_ | @ctx board
 * @public false
 * @edit careful
 */

// SpecRefs: ZVX-DEV-5990, ZVX-DEV-5637, ZVX-DEV-3615, ZVX-DEV-6014

import { useCallback, useState } from 'react';

// @ts-expect-error -- plain .mjs helper beside the dialog; see its JSDoc for the shape (the directive App.tsx carried).
import { planSpaceDeleteRequest } from './spaceDeleteRequest.mjs';

/**
 * ZVX-DEV-5990 — WHY THIS FILE EXISTS. The state, the two callbacks and the
 * three silent exits of the sidebar «✕» lived in App.tsx (1608 lines), and the
 * only way to say anything about them was to read App.tsx. ZVX-DEV-5958
 * measured the flow through a two-tab Docker harness and found the fourth
 * silent exit was the harness's own instrument; a hook that can be rendered
 * alone would have answered that in a second. Campaign 3 (god-file split):
 * a safe cut — the code moves, the behaviour does not.
 *
 * What stays in App.tsx: the dialog host and the two props it needs.
 */

export type SpaceDeleteDestination = { id: string; name: string };

/**
 * ZVX-DEV-5637 — the Space the person asked to delete, and what they must be
 * told about it. `null` means no question is open, which is also what makes
 * «nothing was deleted» true: the delete happens in `confirm` and nowhere else.
 */
export type PendingSpaceDelete = {
  groupId: string;
  spaceName: string;
  boardCount: number;
  destinations: SpaceDeleteDestination[];
};

type BoardGroupLike = { id: string; name: string };

export type SpaceDeleteFlowDeps<G extends BoardGroupLike = BoardGroupLike> = {
  /** Every Space of the active project, as the sidebar sees them. */
  boardGroups: G[];
  /** The rule the data layer asks (ZVX-DEV-3615): `{ valid, error? }`. */
  canDeleteBoardGroup: (groupId: string, groups: G[]) => { valid: boolean; error?: string };
  /** Every board of the active project — read at the moment of the «✕», never captured. */
  getAllBoards: () => Promise<readonly unknown[]>;
  /** The delete itself (useBoardGroups().remove), with the optional destination for the boards. */
  removeGroup: (groupId: string, opts?: { destinationGroupId: string }) => Promise<unknown>;
  /** Called after the delete with the id that went — App.tsx leaves the view if it was open. */
  onDeleted?: (groupId: string) => void;
  /** Where refusals are announced (ZVX-DEV-3615): defaults to `window`. */
  eventTarget?: Pick<EventTarget, 'dispatchEvent'>;
};

export type SpaceDeleteFlow = {
  pending: PendingSpaceDelete | null;
  /** The «✕»: refuses aloud, or opens the question. Deletes nothing. */
  request: (groupId: string) => Promise<void>;
  /** The dialog's confirm: the only place that deletes. */
  confirm: (destinationGroupId?: string) => Promise<void>;
  /** The dialog's cancel. */
  cancel: () => void;
};

export function useSpaceDeleteFlow<G extends BoardGroupLike>(
  deps: SpaceDeleteFlowDeps<G>,
): SpaceDeleteFlow {
  const { boardGroups, canDeleteBoardGroup, getAllBoards, removeGroup, onDeleted, eventTarget } =
    deps;
  const [pending, setPending] = useState<PendingSpaceDelete | null>(null);

  const announceRefusal = useCallback(
    (reason: string) => {
      const target = eventTarget ?? (typeof window !== 'undefined' ? window : null);
      if (!target) return;
      // ZVX-DEV-6014 -- the name is a literal on purpose: the blast-radius key
      // index reads event names as text and does not resolve constants, so a
      // dispatch through `REFUSED_EVENT` left the banner's test unreached.
      target.dispatchEvent(new CustomEvent('system-space-delete-refused', { detail: { reason } }));
    },
    [eventTarget],
  );

  const request = useCallback(
    async (groupId: string) => {
      const check = canDeleteBoardGroup(groupId, boardGroups);
      if (!check.valid) {
        // ZVX-DEV-3615: a refusal must be SAID. This used to `return` with nothing
        // shown, so a person who clicked ✕ on «Все проекты» — a control the
        // sidebar drew and the rule then refused — saw no deletion and no reason,
        // which reads as a broken button rather than a protected object.
        announceRefusal(check.error ?? 'unknown');
        return;
      }
      /*
        ZVX-DEV-5637 — ask, instead of deleting and telling nobody.

        The operator made a Space, put three boards in it, deleted it, and found
        them in «Входящие» with nothing asked. The house already asks one level
        down — deleting a board asks where its tasks go — and the Space was the
        gap. Nothing is written here: this opens the question, and `confirm` is
        the only place that deletes.
      */
      const boards = await getAllBoards();
      const plan = planSpaceDeleteRequest(groupId, boardGroups, boards);
      if (plan.refused || !plan.space) {
        announceRefusal('refused');
        return;
      }
      setPending({
        groupId,
        spaceName: plan.space.name,
        boardCount: plan.boardCount,
        destinations: plan.destinations.map((g: G) => ({ id: g.id, name: g.name })),
      });
    },
    [boardGroups, canDeleteBoardGroup, getAllBoards, announceRefusal],
  );

  const confirm = useCallback(
    async (destinationGroupId?: string) => {
      const current = pending;
      setPending(null);
      if (!current) return;
      await removeGroup(current.groupId, destinationGroupId ? { destinationGroupId } : undefined);
      onDeleted?.(current.groupId);
    },
    [pending, removeGroup, onDeleted],
  );

  const cancel = useCallback(() => setPending(null), []);

  return { pending, request, confirm, cancel };
}
